/**
 * 时区显示 Mixin
 *
 * 目标：把页面上"北京时间字符串"按用户系统时区显示，支持在北京时间和当地时间之间切换。
 *      不改 Vue data，不改 input/textarea/select 的值，不影响表单提交，仅做 DOM 文本替换。
 * *
 * 触发条件：
 *   - 进页面时一次性确定，整页生命周期不再改变
 *   - 仅当宿主组件 computed 提供 isTimezoneEnabled 且为 true 时启用
 *   - 启用时把目标时区设为 Intl 报告的系统时区；若系统时区就是上海，
 *     等价于不转换，依旧零开销
 *
 * 安全设计：
 *   - 跳过 INPUT / TEXTAREA / SELECT / [contenteditable] 及其子节点
 *   - 跳过 [data-no-tz] 子树（业务可主动豁免）
 *   - 仅识别"日期 + 时分(秒)"的完整格式；纯日期/纯时间不转换
 *   - IP 地址、前缀字母等非日期模式自动排除
 *   - 替换失败时保留原文，不破坏页面
 * 
 * 对外暴露（供 TimezoneSwitcher 组件调用）：
 *   - tzCurrentMode: 'local' | 'beijing'（当前显示模式）
 *   - tzSwitchToLocal(): 切换到当地时间
 *   - tzSwitchToBeijing(): 切换回北京时间
 *   - tzSystemTimezone: 系统时区标识（如 'Asia/Tokyo'）
 *
 * 使用方式：
 *   import timezoneMixin from '@/utils/timezoneMixin';
 *   mixins: [timezoneMixin]
 *   宿主组件需要在 computed 提供 isTimezoneEnabled
 */

/**
 * 获取系统时区标识（工具函数，可供其他模块调用）
 * @returns {string} 时区标识，如 'Asia/Shanghai'、'Asia/Tokyo'，获取失败时返回 'Asia/Shanghai'
 */
export function getSystemTimezone() {
  try {
    if (typeof Intl === 'undefined' || !Intl.DateTimeFormat) return DEFAULT_SOURCE_TZ;
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz || DEFAULT_SOURCE_TZ;
  } catch (e) {
    return DEFAULT_SOURCE_TZ;
  }
}

/**
 * 上报时区生效事件到 Aegis
 * @param {Object} vm - 组件实例（需要能访问 this.$store, this.$route, this.processDefinitionKey）
 * @param {string} timezone - 系统时区标识
 */
function reportTimezoneActive(vm, timezone) {
  try {
    const { aegis } = vm.$store.state.user;
    if (!aegis) return;

    // 当前路由路径（优先 vue-router；兜底 window.location）
    let routePath = '';
    try {
      routePath = (vm.$route && vm.$route.path)
        || (typeof window !== 'undefined' && window.location && window.location.pathname)
        || '';
    } catch (e) { /* ignore */ }

    aegis.infoAll({
      msg: JSON.stringify({
        name: 'timezone_active',
        ext1: timezone || '',
        ext2: '',
        ext3: routePath,
        component: 'timezoneMixin',
        processDefinitionKey: vm.processDefinitionKey || '',
        timestamp: new Date().toISOString(),
      }),
      ext1: 'timezone-active',
      trace: 'timezone_active',
    });
  } catch (e) {
    // 上报失败不影响业务
    console.warn('[timezoneMixin] 时区上报失败:', e);
  }
}

// ============ 常量 ============
const DEFAULT_SOURCE_TZ = 'Asia/Shanghai';
const DEFAULT_SOURCE_OFFSET_MIN = 8 * 60;

const OBSERVER_DEBOUNCE_MS = 400;
const SKIP_TAG = new Set(['SCRIPT', 'STYLE', 'INPUT', 'TEXTAREA', 'SELECT', 'OPTION']);
const SKIP_SELECTOR = '[data-no-tz], [contenteditable="true"], [data-no-translate="true"]';

// 配置项：正则版本选择（'new' = 带 Lookbehind 的新版，'old' = 兼容版）
const CFG_TIMEZONE_REGEX_VERSION = 'timezone_regex_version';

// 新版正则字符串（带 Lookbehind，iOS Safari < 16.4 不支持）
const DATETIME_PATTERN_NEW_STR = '(?<![a-zA-Z])'
  + '(\\d{4})'            // 捕获组1: 年
  + '([-/.])'             // 捕获组2: 日期分隔符
  + '(\\d{1,2})'          // 捕获组3: 月
  + '\\2'                 // 反向引用分隔符
  + '(\\d{1,2})'          // 捕获组4: 日
  + '(?:[ T])'
  + '(\\d{1,2})'          // 捕获组5: 时
  + ':'
  + '(\\d{1,2})'          // 捕获组6: 分
  + '(?::(\\d{1,2}))?'    // 捕获组7: 秒（可选）
  + '(?:\\.(\\d{1,3}))?'   // 捕获组8: 毫秒（可选）
  + '(Z|[+\\-]\\d{2}:?\\d{2})?';  // 捕获组9: 时区偏移（可选）

// 旧版兼容正则字符串（不含 Lookbehind）
const DATETIME_PATTERN_OLD_STR = '(^|[^a-zA-Z])'
  + '(\\d{4})'            // 捕获组2: 年
  + '([-/.])'             // 捕获组3: 日期分隔符
  + '(\\d{1,2})'          // 捕获组4: 月
  + '\\3'                 // 反向引用分隔符
  + '(\\d{1,2})'          // 捕获组5: 日
  + '(?:[ T])'
  + '(\\d{1,2})'          // 捕获组6: 时
  + ':'
  + '(\\d{1,2})'          // 捕获组7: 分
  + '(?::(\\d{1,2}))?'    // 捕获组8: 秒（可选）
  + '(?:\\.(\\d{1,3}))?'   // 捕获组9: 毫秒（可选）
  + '(Z|[+\\-]\\d{2}:?\\d{2})?';  // 捕获组10: 时区偏移（可选）

// 兜底正则（永远不匹配）
const DATETIME_PATTERN_FALLBACK = /(?!)/g;

/**
 * 根据配置创建正则表达式
 * @param {Object} context - 组件实例或包含 getConfigItem 的对象
 * @returns {Object} { pattern: RegExp, useLookbehind: boolean }
 */
function createDatetimePattern(context) {
  const configVersion = context.getConfigItem
    ? context.getConfigItem(CFG_TIMEZONE_REGEX_VERSION)
    : 'old';
  const useNew = configVersion === 'new';

  if (useNew) {
    // 尝试创建新版正则（带 Lookbehind）
    try {
      const pattern = new RegExp(DATETIME_PATTERN_NEW_STR, 'g');
      return { pattern, useLookbehind: true };
    } catch (e) {
      console.warn('[timezoneMixin] 浏览器不支持 Lookbehind，降级到兼容版:', e);
      // 降级到旧版
    }
  }

  // 创建旧版兼容正则
  try {
    const pattern = new RegExp(DATETIME_PATTERN_OLD_STR, 'g');
    return { pattern, useLookbehind: false };
  } catch (e) {
    console.error('[timezoneMixin] 兼容版正则创建失败:', e);
    return { pattern: DATETIME_PATTERN_FALLBACK, useLookbehind: false };
  }
}

export default {
  data() {
    return {
      // 当前显示模式：'local'（当地时间） | 'beijing'（北京时间/原文）
      tzCurrentMode: 'local',
      // 系统时区标识
      tzSystemTimezone: DEFAULT_SOURCE_TZ,
      // 日期时间正则模式（实例变量，根据配置动态选择）
      tzDatetimePattern: null,
      // 是否使用带 Lookbehind 的新版正则
      tzUseLookbehind: false,
    };
  },

  created() {
    this._tzOriginalTextMap = new WeakMap();
    this._tzRecordedNodes = new WeakSet();
    this._tzObservers = [];
    this._tzObserverTimer = null;
    this._tzApplying = false;
    this._tzDestroyed = false;
    this._tzEnabled = false;
    this._tzWatcher = null;
    // 初始化日期时间正则（根据配置选择新版或旧版）
    this._tzInitDatetimePattern();
  },

  mounted() {
    if (this._tzShouldEnable()) {
      this._tzEnableOnce();
      return;
    }
    this._tzWatcher = this.$watch('isTimezoneEnabled', (val) => {
      if (val !== true) return;
      if (this._tzEnabled) return;
      this._tzEnableOnce();
      if (typeof this._tzWatcher === 'function') {
        this._tzWatcher();
        this._tzWatcher = null;
      }
    });
  },

  beforeDestroy() {
    this._tzDestroyed = true;
    if (typeof this._tzWatcher === 'function') {
      try { this._tzWatcher(); } catch (e) { /* noop */ }
      this._tzWatcher = null;
    }
    this._tzObservers.forEach((o) => {
      try { o.disconnect(); } catch (e) { /* noop */ }
    });
    this._tzObservers = [];
    if (this._tzObserverTimer) clearTimeout(this._tzObserverTimer);
  },

  methods: {
    // ============ 对外接口：切换时区模式 ============

    /**
     * 切换到当地时间（用系统时区替换页面上的北京时间）
     */
    tzSwitchToLocal() {
      if (!this._tzEnabled) return;
      if (this.tzCurrentMode === 'local') return;
      this.tzCurrentMode = 'local';
      this._tzApplyAll();
    },

    /**
     * 切换回北京时间（还原为原始文本）
     */
    tzSwitchToBeijing() {
      if (!this._tzEnabled) return;
      if (this.tzCurrentMode === 'beijing') return;
      this.tzCurrentMode = 'beijing';
      this._tzRestoreAll();
    },

    // ============ 启用判定 ============

    _tzEnableOnce() {
      if (this._tzEnabled || this._tzDestroyed) return;
      const sysTz = this._tzGetSystemTimezone();
      this.tzSystemTimezone = sysTz;
      this._tzEnabled = true;
      this._tzSetupObservers();
      // 系统时区就是上海 → 不需要转换
      if (sysTz === DEFAULT_SOURCE_TZ) return;
      
      // 上报时区生效事件
      try {
        reportTimezoneActive(this, sysTz);
      } catch (e) {
        console.warn('[timezoneMixin] 时区上报失败:', e);
      }
      
      // 默认显示当地时间
      this.tzCurrentMode = 'local';
      this.$nextTick(() => this._tzApplyAll());
    },

    _tzShouldEnable() {
      try {
        return this.isTimezoneEnabled === true;
      } catch (e) {
        return false;
      }
    },

    /**
     * 初始化日期时间正则表达式（根据配置选择新版或旧版）
     */
    _tzInitDatetimePattern() {
      try {
        const result = createDatetimePattern(this);
        this.tzDatetimePattern = result.pattern;
        this.tzUseLookbehind = result.useLookbehind;
      } catch (e) {
        console.error('[timezoneMixin] 正则初始化失败，使用兜底正则:', e);
        this.tzDatetimePattern = DATETIME_PATTERN_FALLBACK;
        this.tzUseLookbehind = false;
      }
    },

    _tzGetSystemTimezone() {
      try {
        if (typeof Intl === 'undefined' || !Intl.DateTimeFormat) return DEFAULT_SOURCE_TZ;
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        return tz || DEFAULT_SOURCE_TZ;
      } catch (e) {
        return DEFAULT_SOURCE_TZ;
      }
    },

    // ============ 核心：扫描 + 替换 ============

    _tzApplyAll() {
      if (this._tzDestroyed) return;
      if (!this.$el || this.tzSystemTimezone === DEFAULT_SOURCE_TZ) return;
      this._tzApplying = true;
      try {
        const tz = this.tzSystemTimezone;
        this._tzWalkTextNodes(this.$el, (node) => {
          this._tzReplaceNode(node, tz);
        });
      } finally {
        Promise.resolve().then(() => { this._tzApplying = false; });
      }
    },

    _tzRestoreAll() {
      if (!this.$el) return;
      this._tzApplying = true;
      try {
        this._tzWalkTextNodes(this.$el, (node) => {
          if (this._tzOriginalTextMap.has(node)) {
            const original = this._tzOriginalTextMap.get(node);
            if (node.nodeValue !== original) {
              node.nodeValue = original;
            }
          }
        });
      } finally {
        Promise.resolve().then(() => { this._tzApplying = false; });
      }
    },

    _tzWalkTextNodes(root, visitor) {
      if (!root || !root.isConnected) return;
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode: (node) => {
          const parent = node.parentElement;
          if (!parent) return NodeFilter.FILTER_REJECT;
          if (SKIP_TAG.has(parent.tagName)) return NodeFilter.FILTER_REJECT;
          if (parent.closest && parent.closest(SKIP_SELECTOR)) return NodeFilter.FILTER_REJECT;
          if (!/\d/.test(node.nodeValue)) return NodeFilter.FILTER_SKIP;
          return NodeFilter.FILTER_ACCEPT;
        },
      });
      let n;
      while ((n = walker.nextNode())) {
        visitor(n);
      }
    },

    _tzReplaceNode(node, tz) {
      // 兜底检查：确保正则已初始化
      if (!this.tzDatetimePattern) {
        console.error('[timezoneMixin] ❌ 正则未初始化，跳过替换');
        return;
      }

      const original = this._tzOriginalTextMap.has(node)
        ? this._tzOriginalTextMap.get(node)
        : node.nodeValue;
      let replaced;
      try {
        if (this.tzUseLookbehind) {
          // 新版正则（带 Lookbehind）：无前导字符捕获组
          replaced = original.replace(this.tzDatetimePattern, (match, y, sep, mo, d, h, mi, s, ms, off) => {
            const formatted = this._tzFormat({
              y: Number(y),
              mo: Number(mo),
              d: Number(d),
              h: Number(h),
              mi: Number(mi),
              s: s != null ? Number(s) : 0,
              ms: ms != null ? Number(String(ms).padEnd(3, '0').slice(0, 3)) : 0,
              off,
            }, tz, match, sep);
            return formatted == null ? match : formatted;
          });
        } else {
          // 旧版兼容正则：有前导字符捕获组
          replaced = original.replace(this.tzDatetimePattern, (match, prefix, y, sep, mo, d, h, mi, s, ms, off) => {
            const datetimeMatch = match.slice(prefix.length);
            const formatted = this._tzFormat({
              y: Number(y),
              mo: Number(mo),
              d: Number(d),
              h: Number(h),
              mi: Number(mi),
              s: s != null ? Number(s) : 0,
              ms: ms != null ? Number(String(ms).padEnd(3, '0').slice(0, 3)) : 0,
              off,
            }, tz, datetimeMatch, sep);
            return formatted == null ? match : prefix + formatted;
          });
        }
      } catch (err) {
        console.warn('[timezoneMixin] 替换失败，保留原文:', err);
        return;
      }
      if (replaced === original) return;
      if (!this._tzRecordedNodes.has(node)) {
        this._tzOriginalTextMap.set(node, original);
        this._tzRecordedNodes.add(node);
      }
      if (node.nodeValue !== replaced) {
        node.nodeValue = replaced;
      }
    },

    _tzFormat(parts, tz, originalMatch, dateSep) {
      let utcMs;
      if (parts.off) {
        const iso = `${this._tzPad(parts.y, 4)}-${this._tzPad(parts.mo, 2)}-${this._tzPad(parts.d, 2)}`
          + `T${this._tzPad(parts.h, 2)}:${this._tzPad(parts.mi, 2)}:${this._tzPad(parts.s, 2)}`
          + (parts.ms ? `.${this._tzPad(parts.ms, 3)}` : '')
          + (parts.off === 'Z' ? 'Z' : this._tzNormalizeOffset(parts.off));
        const t = Date.parse(iso);
        if (Number.isNaN(t)) return null;
        utcMs = t;
      } else {
        const localMs = Date.UTC(parts.y, parts.mo - 1, parts.d, parts.h, parts.mi, parts.s, parts.ms);
        if (Number.isNaN(localMs)) return null;
        utcMs = localMs - DEFAULT_SOURCE_OFFSET_MIN * 60 * 1000;
      }
      try {
        const hasSeconds = /:\d{1,2}:\d{1,2}/.test(originalMatch);
        const fmt = new Intl.DateTimeFormat('zh-CN', {
          timeZone: tz,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: hasSeconds ? '2-digit' : undefined,
          hour12: false,
        });
        const partsOut = fmt.formatToParts(new Date(utcMs));
        const get = (type) => {
          const p = partsOut.find((x) => x.type === type);
          return p ? p.value : '';
        };
        const y = get('year');
        const mo = get('month');
        const d = get('day');
        let h = get('hour');
        const mi = get('minute');
        const s = get('second');
        if (h === '24') h = '00';
        const datePart = `${y}${dateSep}${mo}${dateSep}${d}`;
        const timePart = hasSeconds ? `${h}:${mi}:${s}` : `${h}:${mi}`;
        const dtSep = originalMatch.includes('T') ? 'T' : ' ';
        return `${datePart}${dtSep}${timePart}`;
      } catch (err) {
        return null;
      }
    },

    _tzPad(n, len) {
      const s = String(n);
      return s.length >= len ? s : '0'.repeat(len - s.length) + s;
    },

    _tzNormalizeOffset(off) {
      if (!off || off === 'Z') return off;
      if (off.includes(':')) return off;
      return `${off.slice(0, 3)}:${off.slice(3)}`;
    },

    // ============ MutationObserver ============
    _tzSetupObservers() {
      if (!this.$el || this.$el.nodeType !== 1) return;
      const obs = new MutationObserver(() => this._tzOnMutations());
      obs.observe(this.$el, { childList: true, subtree: true, characterData: true });
      this._tzObservers.push(obs);
    },

    _tzOnMutations() {
      if (this._tzDestroyed) return;
      if (!this._tzEnabled) return;
      if (this.tzSystemTimezone === DEFAULT_SOURCE_TZ) return;
      if (this.tzCurrentMode !== 'local') return; // 北京模式下不自动转换新内容
      if (this._tzApplying) return;
      if (this._tzObserverTimer) clearTimeout(this._tzObserverTimer);
      this._tzObserverTimer = setTimeout(() => {
        this._tzObserverTimer = null;
        this._tzApplyAll();
      }, OBSERVER_DEBOUNCE_MS);
    },
  },
};
